// InnovateFlow interface redesign — canonical per-workspace navigation trees.
//
// Section 5.2 of the Interface Redesign Specification: one AppShell whose
// sidebar re-renders to the *active workspace*. Nav here is the single source
// of truth for the sidebar, the breadcrumb section/label lookup, and the help
// system's per-screen keying. Paths point at the app's existing working routes
// so the shell is a pure, additive re-frame (no page 404s); later waves migrate
// deep paths and add the net-new pages (Org Overview, split Account tabs).

import type { LucideIcon } from 'lucide-react'
import {
  Home, FilePlus2, FileSearch, BookOpen, Search,
  Bookmark, Globe, Contact, CheckSquare, Share2, CreditCard, KeyRound,
  SlidersHorizontal, Users, Lock, Fingerprint,
  UsersRound, UserCog, ScrollText, LayoutDashboard,
  LayoutGrid, Inbox, Building, ShieldAlert, LifeBuoy,
} from 'lucide-react'

export type WorkspaceKind = 'personal' | 'organisation' | 'aggregator' | 'admin'

export interface NavLink {
  to: string
  label: string
  icon: LucideIcon
  /** breadcrumb section this link lives under (for wayfinding) */
  section?: string
  /** helpContent.ts key for this screen's ? popover / Resources deep-link */
  helpKey?: string
  /** additional paths that should render this item active (deep routes) */
  match?: string[]
  /**
   * Bug #5837. Query values that make this item the active one, when two items
   * share a pathname and differ only by a query parameter. `/projects?view=atlas`
   * and `/projects` are different destinations to a reader — Figure 5 highlights
   * "My inventions" and shows its count — but navLinkForPath matched on the
   * pathname alone, so the sibling "Projects" row won every time and the
   * breadcrumb read "Personal > Innovations > Projects" on the atlas.
   */
  matchQuery?: Record<string, string[]>
  /** vocab token — when set, label is resolved from the active org vocabulary */
  vocabKey?: string
}

export interface NavGroup {
  label: string
  items: NavLink[]
}

export interface WorkspaceNav {
  kind: WorkspaceKind
  /** groups rendered top-to-bottom in the sidebar */
  groups: NavGroup[]
}

// ── Personal workspace — every authenticated user (collaborators included) ──
const personalNav: WorkspaceNav = {
  kind: 'personal',
  groups: [
    {
      label: '',
      items: [
        { to: '/', label: 'Home', icon: Home, section: 'Home', helpKey: 'home' },
      ],
    },
    {
      // Story #5648 build v2 (the figures' inventor sidebar). Two rows, not
      // four: "Projects" and "My inventions" were the same list under two
      // names, and every deep /project route highlighted the wrong one — so
      // "My inventions" now owns them all (its `match` is what makes an
      // invention page highlight it). "New Disclosure" is the same action the
      // figures call "New invention", renamed to the vocabulary the rest of
      // this build uses. "Add documents" is gone from the nav: uploading is
      // something you do TO an invention, and every invention page offers it.
      label: 'Inventions',
      items: [
        // UX-011 (Rob's D2): this row opens the List — the same place Home's "View all"
        // goes. Atlas and the other views are opt-in tabs on that page, reached by their
        // own ?view= URL, so every view still highlights this row by pathname.
        { to: '/projects', label: 'My inventions', icon: LayoutGrid, section: 'Inventions', helpKey: 'projects', match: ['/project', '/projects'] },
        { to: '/new', label: 'New invention', icon: FilePlus2, section: 'Inventions', helpKey: 'new-disclosure', match: ['/submit', '/upload'] },
      ],
    },
    {
      label: 'Research',
      items: [
        { to: '/search', label: 'Search', icon: Search, section: 'Research', helpKey: 'search' },
        { to: '/patents', label: 'Patents', icon: FileSearch, section: 'Research', helpKey: 'patents' },
        { to: '/journals', label: 'Journals', icon: BookOpen, section: 'Research', helpKey: 'journals' },
        { to: '/lists', label: 'Saved Lists', icon: Bookmark, section: 'Research', helpKey: 'lists' },
      ],
    },
    {
      label: 'Network',
      items: [
        { to: '/ecosystem', label: 'Ecosystem', icon: Globe, section: 'Network', helpKey: 'ecosystem', match: ['/ecosystem/'] },
        { to: '/crm', label: 'Contacts & CRM', icon: Contact, section: 'Network', helpKey: 'crm', match: ['/crm/'] },
      ],
    },
    {
      label: 'Work',
      items: [
        { to: '/tasks', label: 'Tasks & Reviews', icon: CheckSquare, section: 'Work', helpKey: 'tasks', match: ['/workflow', '/crm/todos'] },
        { to: '/shared', label: 'Shared with me', icon: Share2, section: 'Work', helpKey: 'shared', match: ['/shared/'] },
      ],
    },
    {
      label: 'Account',
      items: [
        { to: '/account/settings', label: 'Account', icon: SlidersHorizontal, section: 'Account', helpKey: 'account-settings', match: ['/settings'] },
        { to: '/account/security', label: 'Security & Keys', icon: KeyRound, section: 'Account', helpKey: 'account-security', match: ['/encryption'] },
        { to: '/account/billing', label: 'Billing', icon: CreditCard, section: 'Account', helpKey: 'account-billing', match: ['/billing'] },
        { to: '/account/preferences', label: 'Preferences', icon: SlidersHorizontal, section: 'Account', helpKey: 'account-preferences', match: ['/preferences'] },
        { to: '/support', label: 'Support', icon: LifeBuoy, section: 'Account', helpKey: 'support' },
      ],
    },
  ],
}

// ── Organisation workspace — role admin/owner, entered via the switcher ──
const organisationNav: WorkspaceNav = {
  kind: 'organisation',
  groups: [
    {
      label: 'Organisation',
      items: [
        { to: '/org/overview', label: 'Overview', icon: LayoutDashboard, section: 'Organisation', helpKey: 'org-overview' },
        { to: '/org/members', label: 'Members & Licences', icon: Users, section: 'Organisation', helpKey: 'org-members', match: ['/organisation/members'] },
        { to: '/org/access', label: 'Access', icon: Lock, section: 'Organisation', helpKey: 'org-access', match: ['/organisation/access'] },
        { to: '/org/identity', label: 'Identity & SSO', icon: Fingerprint, section: 'Organisation', helpKey: 'org-identity', match: ['/organisation/identity'] },
        { to: '/org/keys', label: 'Encryption Keys', icon: KeyRound, section: 'Organisation', helpKey: 'org-keys', match: ['/organisation/keys'] },
        // Reuses the existing preferences page (it already renders the org-locked
        // policy layer via the 4-layer preference resolver) — same help key as the
        // Account nav item; /preferences stays as a back-compat alias.
        { to: '/org/preferences', label: 'Preferences', icon: SlidersHorizontal, section: 'Organisation', helpKey: 'account-preferences', match: ['/preferences'] },
      ],
    },
  ],
}

// ── Aggregator workspace — aggregator membership; labels adapt to org type ──
// Labels resolve from the active org's vocabulary (vocabKey) at render time.
const aggregatorNav: WorkspaceNav = {
  kind: 'aggregator',
  groups: [
    {
      label: 'Portfolio',
      items: [
        { to: '/agg', label: 'Portfolio', icon: LayoutGrid, section: 'Aggregator', helpKey: 'agg-portfolio', vocabKey: 'portfolio' },
        // Story #5648 build v2, figure S7: the aggregator nav lists Across
        // between Portfolio and Inbound. Without a row of its own,
        // /agg/across had no way in from the sidebar AND highlighted
        // Portfolio once you got there (the prefix rule matched '/agg/'), so
        // the nav said you were somewhere you were not.
        { to: '/agg/across', label: 'Across', icon: Globe, section: 'Aggregator', helpKey: 'agg-across-portfolios' },
        { to: '/agg/inbound', label: 'Inbound', icon: Inbox, section: 'Aggregator', helpKey: 'agg-inbound', vocabKey: 'inbound' },
        { to: '/agg/team', label: 'Team', icon: UsersRound, section: 'Aggregator', helpKey: 'agg-team', match: ['/team'] },
        { to: '/agg/billing', label: 'Billing', icon: CreditCard, section: 'Aggregator', helpKey: 'agg-billing' },
      ],
    },
  ],
}

// ── Platform Admin console — innovateflow-admin group only ──
const adminNav: WorkspaceNav = {
  kind: 'admin',
  groups: [
    {
      label: 'Platform Admin',
      // UX-046 (Rob's D6): no "Platform Settings" row until a real platform
      // settings page exists — it used to open the personal Account page.
      items: [
        { to: '/admin', label: 'Overview', icon: LayoutDashboard, section: 'Platform Admin', helpKey: 'admin-overview' },
        { to: '/admin/users', label: 'Users', icon: UserCog, section: 'Platform Admin', helpKey: 'admin-users' },
        { to: '/admin/organisations', label: 'Organisations', icon: Building, section: 'Platform Admin', helpKey: 'admin-orgs' },
        { to: '/admin/support', label: 'Support', icon: LifeBuoy, section: 'Platform Admin', helpKey: 'admin-support' },
        { to: '/admin/audit', label: 'Audit Log', icon: ScrollText, section: 'Platform Admin', helpKey: 'admin-audit' },
        { to: '/admin/security', label: 'Security', icon: ShieldAlert, section: 'Platform Admin', helpKey: 'admin-security' },
      ],
    },
  ],
}

export const WORKSPACE_NAV: Record<WorkspaceKind, WorkspaceNav> = {
  personal: personalNav,
  organisation: organisationNav,
  aggregator: aggregatorNav,
  admin: adminNav,
}

/** Flatten a workspace's nav to a path→link map for breadcrumb/help lookup. */
export function navLinkForPath(
  nav: WorkspaceNav,
  pathname: string,
  search = '',
): NavLink | undefined {
  const links = nav.groups.flatMap((g) => g.items)
  // Bug #5837: a query-qualified item wins over its plain-path sibling, but
  // only when the current URL actually carries one of its declared values.
  // With no search string nothing here can match, so /projects still resolves
  // to Projects exactly as before.
  if (search) {
    const params = new URLSearchParams(search)
    const qualified = links.find((l) => {
      if (!l.matchQuery) return false
      if (l.to.split('?')[0] !== pathname) return false
      return Object.entries(l.matchQuery).every(([key, values]) => {
        const actual = params.get(key)
        return actual !== null && values.includes(actual)
      })
    })
    if (qualified) return qualified
  }
  // Exact match first, then longest prefix / declared deep-route match.
  const exact = links.find((l) => l.to === pathname)
  if (exact) return exact
  return links
    .filter((l) => pathname.startsWith(l.to + '/') || (l.match ?? []).some((m) => pathname.startsWith(m)))
    .sort((a, b) => b.to.length - a.to.length)[0]
}

// ── Direct-URL workspace derivation ──────────────────────────────────────
//
// The switcher is not the only way a workspace gets entered — a bookmark, a
// shared link, or a browser refresh lands directly on an org/aggregator/admin
// route. Without this, the shell rendered whatever workspace was last active
// (usually Personal), so the sidebar/pill disagreed with the URL and org-scoped
// queries kept using the wrong (or no) entity id. Each prefix below maps a
// pathname to the workspace kind that owns it; anything else (including "/")
// is a personal/shared route and is intentionally left unmapped so an
// in-progress Personal (or other) session is never force-switched by it.
const ROUTE_KIND_PREFIXES: Array<{ prefix: string; kind: WorkspaceKind }> = [
  { prefix: '/organisation', kind: 'organisation' },
  { prefix: '/org', kind: 'organisation' },
  { prefix: '/agg', kind: 'aggregator' },
  { prefix: '/team', kind: 'aggregator' },
  { prefix: '/admin', kind: 'admin' },
]

/** The workspace kind a pathname belongs to, or undefined for personal/shared routes. */
export function workspaceKindForPath(pathname: string): WorkspaceKind | undefined {
  const hit = ROUTE_KIND_PREFIXES.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
  return hit?.kind
}

/**
 * Given the current pathname, the workspaces available to this user, and the
 * currently active workspace, returns the workspace a direct-URL navigation
 * should switch to — or `undefined` when no switch is needed: a personal/
 * shared route, the active workspace already matches the route's kind, or the
 * user has no authorised workspace of that kind (page-level gates handle the
 * "not authorised" message; the shell simply doesn't force a switch).
 */
export function resolveActiveWorkspaceForRoute<W extends { kind: WorkspaceKind }>(
  pathname: string,
  workspaces: W[],
  active: W,
): W | undefined {
  const routeKind = workspaceKindForPath(pathname)
  if (!routeKind || active.kind === routeKind) return undefined
  return workspaces.find((w) => w.kind === routeKind)
}
